<script setup lang="ts">
import type { IProps } from './career-main-recruitmentProcess.types'

defineProps<IProps>()

const returnSiteClasss = (index: number) => {
  return index % 2 === 1 ? 'Recruitment-Process__cardLines--left' : 'Recruitment-Process__cardLines--right'
}

const returnDotSiteClass = (index: number) => {
  return index % 2 === 1 ? 'Recruitment-Process__cardDot--left' : 'Recruitment-Process__cardDot--right'
}

const returnLastClass = (index: number, arrayLength: number) => {
  return index === arrayLength ? 'Recruitment-Process__cardLines--last' : ''
}
</script>

<template>
  <div class="Recruitment-Process">
    <div class="Recruitment-Process__container">
      <div class="Recruitment-Process__wrapper">
        <div class="Recruitment-Process__headerContainer">
          <UiHeader
            :header="header"
            :subheader="subheader"
            position="center"
            header-size="h5"
            subheader-size="body-1"
            subheader-size-mobile?="body-1"
          />
        </div>
        <div class="Recruitment-Process__cards">
          <div
            v-for="(card, index) in cards"
            :key="card.title"
            :class="['Recruitment-Process__card', `${index%2 === 1 ? 'Recruitment-Process__card--right' : ''}`]"
          >
            <div class="Recruitment-Process__cardText">
              <p class="Recruitment-Process__cardTitle">
                {{ card.title }}
              </p>

              <p class="Recruitment-Process__cardDescription">
                {{ card.description }}
              </p>

              <div :class="['Recruitment-Process__cardLines', `${returnSiteClasss(index)}`, `${returnLastClass(index, cards.length-1)}`]">
                <div :class="['Recruitment-Process__cardDot', `${returnDotSiteClass(index)}`]">
                  <div class="Recruitment-Process__cardDotInner" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.Recruitment-Process {
  width: 100%;

  &__container {
    display: flex;
    justify-content: center;
    padding: 4rem 1.6rem;
    background-color: $purple-50;
    border-radius: 2rem;
  }

  &__wrapper {
    max-width: 136rem;
    width: 100%;
    text-align: center;
  }

  &__headerContainer {
    max-width: 110rem;
    margin: 0 auto 4rem auto;

    :deep(.ui-header) {
      font-weight: 500;
      font-size: 4.4rem;
      line-height: 5.7rem;
    }
  }

  &__cards {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__card {
    margin-bottom: 1.6rem;
    padding-left: 4.8rem;
  }

  &__cardText {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-width: 55.8rem;
    width: 100%;
    padding: 2.4rem 2rem 2.4rem 3.2rem;
    background-color: $white;
    border-radius: 2.4rem;
  }

  &__cardTitle {
    @include h10;
    margin-bottom: 0.8rem;
  }

  &__cardDescription {
    @include body-2-medium;
    text-align: left;
  }

  &__cardLines {
    position: absolute;
    left: -3.2rem;
    width: 3.2rem;
    height: calc(100% + 16px);

    &--left {
      border-top: 1px dashed $purple-300;
      border-left: 1px dashed $purple-300;
    }

    &--right {
      border-top: 1px dashed $purple-300;
      border-left: 1px dashed $purple-300;
    }

    &--last {
      border-right: none;
      border-left: none;
    }
  }

  &__cardDot {
    position: absolute;
    top: -1.6rem;
    left: -1.6rem;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 3.2rem;
    height: 3.2rem;
    background-color: $purple-100;
    border-radius: 50%;
  }

  &__cardDotInner {
    width: 1.6rem;
    height: 1.6rem;
    background-color: $purple-400;
    border-radius: 50%;
  }
}

@include atM {
  .Recruitment-Process {
    padding: 0 1.6rem;

    &__container {
      padding: 12rem 0;
    }

    &__headerContainer {
      margin: 0 auto 7.9rem auto;
    }

    &__card {
      position: relative;
      display: flex;
      padding: 0;
    }

    &__cardText {
      width: 55.8rem;
      padding: 4rem 8.6rem 4rem 3.3rem;
    }

    &__cardTitle {
      @include h9;
    }
  }
}

@media(min-width: 1500px) {
  .Recruitment-Process {

    &__card {
      margin-bottom: -8rem;

      &:last-child {
        margin-bottom: 0;
      }

      &--right {
        justify-content: flex-end;
      }
    }

    &__cards {
      align-items: unset;
    }

    &__cardLines {
      width: 12.25rem;
      height: calc(100% - 8rem);

      &--left {
        right: unset;
        left: -12.25rem;
        border-right: none;
        border-left: 1px dashed $purple-300;
      }

      &--right {
        right: -12.25rem;
        left: unset;
        border-right: 1px dashed $purple-300;
        border-left: none;
      }

      &--last {
        border-right: none;
        border-left: none;
      }
    }

    &__cardDot {
      &--left {
        top: -1.6rem;
        right: unset;
        left: -1.6rem;
      }

      &--right {
        top: -1.6rem;
        right: -1.6rem;
        left: unset;
      }
    }
  }
}
</style>
